import {announcement_getsByClassesOwner_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import classNames from 'classnames'
import {Button} from 'antd'
import {organizationPermissionCodes} from 'modules/permissions/contants'
import React, {useContext, useMemo} from 'react'
import {useLocation} from 'react-use'
import {compose, fromRenderProps, nest} from 'recompose'
import {LayoutContext} from '../../../components/layouts/Default/LayoutContext'
import {useRouteSearchParam} from '../../../envs/_current/history'
import {createCheckPermissions,} from 'views/AppLocals/factory/createCheckPermissions'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import {Queries} from 'views/Shared/enums/Queries'
import {useMenuHandler} from 'views/Shared/handlers/useMenuHandler'
import CRUD from 'views/Shared/enums/CRUD'
import WorkspaceChannelList from '../Container/WorkspaceChannelList'
import WorkspaceEventList from '../Container/WorkspaceEventList'
import WorkspaceContext from '../WorkspaceContext'
import {WorkspaceScopeGuide} from '../components/WorkspaceScopeGuide'
import WorkspaceOrganizationAnnouncements, {renderOrganizationAnnouncementWorkspace} from "../Insights/WorkspaceOrganizationAnnouncements";
import UnderDevelopment from "../../Shared/components/UnderDevelopment"
import WorkspaceModerationList from './containers/WorkspaceModerationList';
import useTranslate from "../../../modules/local/useTranslate";
import _ from "lodash";
import {useAnnouncementForm} from "../../AppLocals/Announcement/useAnnouncementForm";
import WorkspaceActivityCalendar from "../HomePage/WorkspaceActivityCalendar";
import MainContext from "../../MainPage/MainContext";
import renderLoading from "../../MainPage/functions/renderPagingLoading";

const ItemKeys = Object.freeze({
    ANNOUNCEMENTS:     'announcements',
    ARTICLES:          'articles',
    EVENTS:            'events',
    CHANNELS:          'channels',
    ACTIVITY_SCHEDULE: 'activity-schedule',
    MEDIA_LIBRARY:     'media-library',
    MODERATION:        'moderation',
})

// Prefix riêng để không đụng URL với query_params mặc định (sort-by/keyword) mà
// WorkspaceOrganizationAnnouncements đang dùng cho tab Cộng đoàn - 2 tab không mount cùng lúc nhưng
// vẫn giữ state URL độc lập, giống cách Lịch sinh hoạt tách org-/cls- (xem TimetableScheduleFilter).
const courseAnnouncementFilterActions = {
    sortBy: 'course-ann-sort-by',
    keyword: 'course-ann-keyword',
}

// Tab "Thông báo (Khóa học)" - gộp read-only thông báo của mọi Lớp học trong Cộng đoàn (tương tự
// tab Thời khóa biểu ở Lịch sinh hoạt/WorkspaceActivityCalendar). KHÔNG có nút tạo/sửa/xoá: thông
// báo lớp học hiện chỉ được tạo/quản lý từ app Giáo viên (WorkingApp), Workspace admin chưa có màn
// theo từng lớp để trỏ tới nên không thêm callout hướng dẫn tạo ở đây.
const WorkspaceCourseAnnouncements = ({organization_id}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const location = useLocation()

    const keyword = useRouteSearchParam(courseAnnouncementFilterActions.keyword)

    const refresh_deps = useMemo(
        () => [
            organization_id,
            keyword,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [organization_id, keyword, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        isToggleDefault: false,
        AdvancedFilter: NullAsDefault,
        query_params: courseAnnouncementFilterActions,
        withKey: createPagerKey(refresh_deps),
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: announcement_getsByClassesOwner_Api,
            query: bindQueryParams([{id: organization_id}, {type: 'organization'}]),
            renderItem: renderOrganizationAnnouncementWorkspace({
                translate: t,
                isLg,
                actionMenu: false,
            }),
            renderWrap: (child) => (
                <div className="mt-3 flex flex-col divide-y divide-gray-100 list-item-workspace">
                    {child}
                </div>
            ),
        },
        onInit: ({onFilterChange = NullAsDefault}) => {
            onFilterChange({keyword})
        },
        filter_params: {keyword},
        dependencies: [organization_id, keyword],
    })

    return (
        <div className="flex flex-col">
            {pager_widget}
            <div className="h-16"/>
        </div>
    )
}

const AnnouncementTabKeys = Object.freeze({
    ORGANIZATION: 'org-announcements',
    COURSE: 'course-announcements',
})

const AnnouncementsTab = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)
    const groups = _.get(organization, 'groups', [])
    const organization_id = getId(organization)

    const {modal, handleAction, refresh_token} = useAnnouncementForm({
        owner_type: 'organization',
        ownerItem: organization,
        groups,
        notifyListeners: {},
    })

    const {activeKey, onMenuSelect} = useMenuHandler({
        routes: [
            {key: AnnouncementTabKeys.ORGANIZATION, path: AnnouncementTabKeys.ORGANIZATION},
            {key: AnnouncementTabKeys.COURSE, path: AnnouncementTabKeys.COURSE},
        ],
        actionName: Queries.TAB,
        defaultKey: AnnouncementTabKeys.ORGANIZATION,
    })

    // Cùng convention "Cộng đoàn"/"Khóa học" đang dùng ở Tổng quan > Thông báo (WorkspaceNotifications)
    // - tái dùng nguyên 2 key dịch sẵn có, không cần thêm string mới.
    const tabs = [
        {key: AnnouncementTabKeys.ORGANIZATION, label: 'organization announcements'},
        {key: AnnouncementTabKeys.COURSE, label: 'course announcements'},
    ]

    // Format (title + mô tả tổng quan → tab → callout theo tab → danh sách) giống hệt trang Lịch
    // sinh hoạt/WorkspaceActivityCalendar để 2 màn tổng hợp trong Truyền thông nhất quán 1 kiểu.
    // Nút "Tạo mới" đưa lên đây (chỉ hiện ở tab Cộng đoàn) - dùng handleAction/refresh_token của
    // chính AnnouncementsTab, WorkspaceOrganizationAnnouncements nhận hideTitleBar để không lặp lại
    // title+nút của chính nó (search/filter riêng của nó vẫn giữ nguyên).
    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('announcements')}
                    </h2>
                    <div className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <span className="mt-2 text-xs text-color-300 tracking-wide">
                        {t('announcements page description')}
                    </span>
                </div>
                {activeKey === AnnouncementTabKeys.ORGANIZATION && (
                    <Button
                        color="primary" variant="solid"
                        className="rounded-lg no-border flex flex-center font-semibold"
                        onClick={() => handleAction(CRUD.CREATE)}>
                        {t('create')}
                    </Button>
                )}
            </div>
            <div className="mr-12 flex gap-3 z-3">
                {tabs.map(({key, label}) => (
                    <button
                        key={key}
                        onClick={() => onMenuSelect(key)}
                        style={{minHeight: 40}}
                        className={classNames(
                            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition-all duration-300',
                            activeKey === key
                                ? 'bg-secondary-500 text-white'
                                : 'background border border-color-50 hover:border-secondary-500 text-color-200 hover:bg-secondary-50 hover:text-secondary-500 shadow-sm'
                        )}>
                        {t(label)}
                    </button>
                ))}
            </div>
            {activeKey === AnnouncementTabKeys.ORGANIZATION ? (
                <WorkspaceScopeGuide description="organization announcements guide description"/>
            ) : (
                <WorkspaceScopeGuide description="course announcements guide description"/>
            )}
            {activeKey === AnnouncementTabKeys.ORGANIZATION ? (
                <WorkspaceOrganizationAnnouncements hideTitleBar refreshToken={refresh_token}/>
            ) : (
                <WorkspaceCourseAnnouncements organization_id={organization_id}/>
            )}
            {modal}
            <div className="h-16"/>
        </div>
    )
}

const routeEntities = [
    {
        key: ItemKeys.ANNOUNCEMENTS,
        component: AnnouncementsTab,
    },
    {
        key: ItemKeys.ARTICLES,
        component: UnderDevelopment,
    },
    {
        key: ItemKeys.CHANNELS,
        component: createCheckPermissions([
            organizationPermissionCodes.organizationManageChannel,
        ])(WorkspaceChannelList),
    },
    {
        key: ItemKeys.EVENTS,
        component: createCheckPermissions([
            organizationPermissionCodes.organizationManageEvent,
        ])(WorkspaceEventList),
    },
    {
        key: ItemKeys.ACTIVITY_SCHEDULE,
        component: WorkspaceActivityCalendar,
    },
    {
        key: ItemKeys.MEDIA_LIBRARY,
        component: UnderDevelopment,
    },
    {
        key: ItemKeys.MODERATION,
        component: WorkspaceModerationList,
    }
].map(({key, ...rest}) => ({
    key,
    path: key,
    ...rest,
}))

const WorkspaceActivities = ({owner_id, owner_type}) => {
    const {isLoading} = useContext(MainContext)

    const {activeKey, Component, onMenuSelect} = useMenuHandler({
        routes: routeEntities,
        actionName: Queries.REL,
    })

    return (
        <div className="px-3 md:px-6">
            {!!isLoading ? renderLoading() : <Component/>}
        </div>
    )
}

export default nest(
    // withProps({lazy: true})(DelayRender),
    compose(
        fromRenderProps(WorkspaceContext.Consumer, ({organization}) => ({
            owner_id: getId(organization),
            owner_type: getType(organization),
        }))
    )(WorkspaceActivities)
)
